// src/pages/Permission.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  MdSecurity,
  MdListAlt,
  MdSearch,
  MdEdit,
  MdDelete,
  MdSave,
  MdShield,
  MdLock,
  MdClearAll,
  MdDoneAll,
} from "react-icons/md";

/* Brand palette (matches events/venues page) */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  page: "#f5f6f8",
};

export default function Permission() {
  const [role, setRole] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [popup, setPopup] = useState({
    visible: false,
    message: "",
    type: "success",
  });
  const [data, setData] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [query, setQuery] = useState("");

  // ✅ Keep this array stable
  const allPermissions = useMemo(
    () => [
      // Core views
      "view_dashboard",
      "view_users",
      "view_artists",
      "view_mentors",
      "view_roles",
      "view_permission",
      "view_employees",
      "view_news",
      "view_artist_reports",
      "view_artist_bookings",
      "view_mentor_bookings",
      "view_approved_events",
      "view_booked_events",
      "view_posts",
      "view_story",
      "view_song_request",
      "view_broadcast",
      "send_broadcast",
      "view_settings",
      "manage_settings",
      "manage_payment_settings",
      "manage_api_settings",
      "manage_authkey_settings",
      "view_authkey_templates",
      "view_authkey_balance",
      "send_authkey_broadcast",
      "view_crm",
      "manage_crm",
      "manage_crm_contacts",
      "manage_crm_templates",
      "send_crm_broadcast",
      "view_crm_reports",
      "view_app_adds",
      "view_artist_verification",
      "view_verified_artists",
      "view_chat_support",
      "manage_chat_support",
      "assign_chat_support",
      "close_chat_support",

      // ✅ NEW: Media optimization monitor page
      "view_media_monitor",

      // Opportunity Board admin
      "view_opportunities",
      "manage_opportunities",
      "moderate_opportunities",
      "feature_opportunities",

      // Event discovery admin
      "view_event_discovery",
      "manage_event_discovery",
      "feature_events",

      // Event/Venue approvals page
      "view_events_venues_approval",
      "view_approved_events_venues",
      "view_event_details",
      "view_venue_details",
      "approve_events",
      "approve_venues",
      "edit_event_status",
      "edit_venue_status",

      // ✅ Event App Banners manager
      "view_event_app_banners",
      "upload_event_app_banners",
      "delete_event_app_banners",
      "replace_event_app_banners",
      "manage_event_app_banners",

      // Specific views/edits used elsewhere
      "view_event_managers",
      "edit_event_managers",
      "edit_mentors",
      "delete_posts",
      "delete_story",
      "edit_mentor_bookings",
      "edit_artist_bookings",
      "delete_artist_bookings",
      "manage_artist_reports",

      // CRUD / management
      "add_user",
      "edit_user",
      "delete_user",
      "manage_artists",
      "manage_artist_pro_profiles",
      "moderate_artist_media",
      "feature_artists",
      "verify_artist",
      "add_artist",
      "edit_artist",
      "delete_artist",
      "manage_events",
      "add_event",
      "edit_event",
      "delete_event",
      "publish_news",
      "edit_news",
      "delete_news",
      "send_notifications",
      "push_notification",
      "view_reports",
      "edit_roles",
      "assign_roles",
      "manage_finance",
      "process_payment",
      "access_support",
      "manage_sales",
      "handle_inquiries",
      "manage_campaigns",
      "create_campaign",
      "edit_campaign",
      "read_analytics",
      "generate_reports",
      "upload_documents",
      "download_data",
    ],
    []
  );

  useEffect(() => {
    // gate
    const employeeRaw = localStorage.getItem("employee");
    const employee = employeeRaw ? JSON.parse(employeeRaw) : null;
    const isSuper = employee?.role === "super_admin";
    const hasPermission = employee?.permissions?.includes("edit_roles");
    setIsSuperAdmin(isSuper);
    setCanEdit(isSuper || hasPermission);

    // roles source
    const unsubEmployees = onSnapshot(collection(db, "employees"), (snap) => {
      const rolesSet = new Set();
      snap.docs.forEach((d) => {
        const r = d.data()?.role;
        if (r) rolesSet.add(r);
      });
      setAvailableRoles([...rolesSet]);
    });

    // permissions assigned per role
    const unsubPermissions = onSnapshot(collection(db, "permissions"), (snap) => {
      const list = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
      setData(list);
    });

    return () => {
      unsubEmployees();
      unsubPermissions();
    };
  }, []);

  const showPopup = (message, type = "success") => {
    setPopup({ visible: true, message, type });
    setTimeout(
      () => setPopup({ visible: false, message: "", type: "success" }),
      3000
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role || selectedPermissions.length === 0) {
      showPopup("Please select a role and at least one permission.", "error");
      return;
    }

    const existing = data.find((item) => item.role === role);

    try {
      if (existing) {
        await updateDoc(doc(db, "permissions", existing.docId), {
          permissions: selectedPermissions,
        });
        showPopup("Permissions updated successfully!");
      } else {
        await addDoc(collection(db, "permissions"), {
          role,
          permissions: selectedPermissions,
        });
        showPopup("Permissions assigned successfully!");
      }

      // optional: keep role after save
      // setRole("");
      setSelectedPermissions([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);
      showPopup("Error saving permissions.", "error");
    }
  };

  const togglePerm = (perm) => {
    setSelectedPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleDelete = async (docId, roleName) => {
    if (
      window.confirm(`Are you sure to delete permissions for role "${roleName}"?`)
    ) {
      try {
        await deleteDoc(doc(db, "permissions", docId));
        showPopup(`Permissions deleted for "${roleName}"`);
      } catch (err) {
        console.error(err);
        showPopup("Failed to delete permissions", "error");
      }
    }
  };

  // Filtered permission list
  const filteredPerms = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allPermissions;
    return allPermissions.filter((p) => p.toLowerCase().includes(q));
  }, [allPermissions, query]);

  // Labels & badges
  const toNiceLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

  const badgeFor = (key) => {
    const k = key.toLowerCase();
    if (k.startsWith("view_")) return { bg: "#e3f2fd", fg: "#1565c0", icon: <MdSearch /> };
    if (k.startsWith("edit_")) return { bg: "#fff8e1", fg: "#ef6c00", icon: <MdEdit /> };
    if (k.startsWith("delete_")) return { bg: "#ffebee", fg: "#c62828", icon: <MdDelete /> };
    if (k.startsWith("add_") || k.startsWith("create_"))
      return { bg: "#ede7f6", fg: "#5e35b1", icon: <MdSave /> };
    if (k.startsWith("upload_"))
      return { bg: "#ede7f6", fg: "#5e35b1", icon: <MdSave /> };
    if (k.startsWith("replace_"))
      return { bg: "#fff8e1", fg: "#ef6c00", icon: <MdEdit /> };
    if (k.startsWith("approve_"))
      return { bg: "#e0f2f1", fg: "#00796b", icon: <MdShield /> };
    if (k.startsWith("publish_"))
      return { bg: "#e8f5e9", fg: "#2e7d32", icon: <FaCheckCircle /> };
    if (k.startsWith("manage_") || k.startsWith("process_"))
      return { bg: "#f3e5f5", fg: "#8e24aa", icon: <MdLock /> };
    if (k.startsWith("send_") || k.startsWith("push_"))
      return { bg: "#f1f8e9", fg: "#558b2f", icon: <MdSave /> };
    return { bg: "#eceff1", fg: "#37474f", icon: <MdLock /> };
  };

  const selectAllFiltered = () =>
    setSelectedPermissions((prev) => {
      const s = new Set(prev);
      filteredPerms.forEach((p) => s.add(p));
      return [...s];
    });

  const clearAll = () => setSelectedPermissions([]);

  const onlyViews = () =>
    setSelectedPermissions((prev) => [
      ...new Set([
        ...prev,
        ...filteredPerms.filter((p) => p.startsWith("view_")),
      ]),
    ]);

  const onlyEdits = () =>
    setSelectedPermissions((prev) => [
      ...new Set([
        ...prev,
        ...filteredPerms.filter((p) => p.startsWith("edit_")),
      ]),
    ]);

  const onlyApprovals = () =>
    setSelectedPermissions((prev) => [
      ...new Set([
        ...prev,
        ...filteredPerms.filter((p) => p.startsWith("approve_")),
      ]),
    ]);

  if (!canEdit) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={40} color="#f44336" />
          <h3 style={{ margin: "1rem 0" }}>Access Denied</h3>
          <p>You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-pro-page" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <div className="aa-pro-hero" style={{ ...hero, marginBottom: 18 }}>
        <div style={heroLeft}>
          <div className="aa-pro-hero-icon">
            <MdSecurity />
          </div>
          <div>
            <h2 className="aa-pro-title">Assign Role Permissions</h2>
            <p className="aa-pro-subtitle" style={{ maxWidth: 640 }}>
              Use search and quick filters to assign fine-grained access. Brand-styled, clean and fast.
            </p>
          </div>
        </div>
        <div className="aa-pro-metrics" style={heroStats}>
          <Badge label="Roles" value={availableRoles.length} />
          <Badge label="Permissions" value={allPermissions.length} />
        </div>
      </div>

      {/* Toast */}
      {popup.visible && (
        <div
          style={{
            ...toastBox,
            background:
              popup.type === "error"
                ? "#fff3cd"
                : `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
            color: popup.type === "error" ? "#856404" : "#fff",
          }}
        >
          {popup.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          <span>{popup.message}</span>
        </div>
      )}

      {/* Form Card */}
      <form onSubmit={handleSubmit} style={{ ...card, borderRadius: 24, boxShadow: "0 24px 60px rgba(15,23,42,0.08)" }}>
        {/* Row: Role select + Selected count */}
        <div style={row}>
          <div style={{ flex: 1 }}>
            <label style={label}>Select Role</label>
            <div style={inputWrap}>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={select}
              >
                <option value="">— Select Role —</option>
                {availableRoles.map((r, idx) => (
                  <option key={idx} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ minWidth: 220 }}>
            <label style={label}>Selected</label>
            <div style={summaryPill}>
              <MdDoneAll />
              {selectedPermissions.length} permission
              {selectedPermissions.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {/* Search + Quick Filters */}
        <div style={{ ...row, alignItems: "flex-end", marginTop: 6 }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Search Permissions</label>
            <div style={searchBox}>
              <MdSearch style={{ opacity: 0.7 }} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter…"
                style={searchInput}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={selectAllFiltered} style={chipBtn}>
              <MdDoneAll /> Select All (filtered)
            </button>
            <button type="button" onClick={onlyViews} style={chipBtn}>
              <MdSearch /> View
            </button>
            <button type="button" onClick={onlyEdits} style={chipBtn}>
              <MdEdit /> Edit
            </button>
            <button type="button" onClick={onlyApprovals} style={chipBtn}>
              <MdShield /> Approvals
            </button>
            <button type="button" onClick={clearAll} style={chipBtnGhost}>
              <MdClearAll /> Clear
            </button>
          </div>
        </div>

        {/* Permission Chips */}
        <div style={chipGrid}>
          {filteredPerms.map((perm) => {
            const meta = badgeFor(perm);
            const active = selectedPermissions.includes(perm);
            return (
              <button
                key={perm}
                type="button"
                onClick={() => togglePerm(perm)}
                title={perm}
                style={{
                  ...permChip,
                  borderColor: active ? "transparent" : COLORS.border,
                  background: active
                    ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                    : meta.bg,
                  color: active ? "#fff" : meta.fg,
                  boxShadow: active
                    ? "0 10px 18px rgba(242,12,83,.15)"
                    : "inset 0 0 0 1px rgba(0,0,0,0.00)",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "grid", placeItems: "center" }}>{meta.icon}</span>
                  {toNiceLabel(perm)}
                </span>
              </button>
            );
          })}
          {filteredPerms.length === 0 && (
            <div style={noneNote}>No permissions match your search.</div>
          )}
        </div>

        {/* Save */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button type="submit" style={primaryBtn}>
            <MdSave />
            Save Permissions
          </button>
        </div>
      </form>

      {/* Assigned Permissions - Cards */}
      <div style={{ ...sectionHeader }}>
        <div style={sectionHeaderLeft}>
          <div style={sectionIcon}>
            <MdListAlt />
          </div>
          <div>
            <div style={sectionTitle}>Assigned Permissions</div>
            <div style={sectionSub}>Per role overview with quick edit/delete.</div>
          </div>
        </div>
      </div>

      <div style={cardsGrid}>
        {data.map((entry, i) => (
          <div key={i} style={roleCard}>
            <div style={roleCardHead}>
              <div style={{ fontWeight: 900, color: COLORS.text }}>{entry.role}</div>
              {isSuperAdmin && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setRole(entry.role);
                      setSelectedPermissions(entry.permissions || []);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    style={smallBtn}
                    title="Edit"
                  >
                    <MdEdit />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(entry.docId, entry.role)}
                    style={smallBtnDanger}
                    title="Delete"
                  >
                    <MdDelete />
                  </button>
                </div>
              )}
            </div>

            <div style={roleCardBody}>
              {(entry.permissions || []).length === 0 ? (
                <div style={noneNote}>No permissions.</div>
              ) : (
                <div style={roleChipWrap}>
                  {entry.permissions.map((perm, idx) => {
                    const meta = badgeFor(perm);
                    return (
                      <span
                        key={idx}
                        style={{ ...roleChip, background: meta.bg, color: meta.fg }}
                      >
                        {toNiceLabel(perm)}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------- Small Components ------------------- */
function Badge({ label, value }) {
  return (
    <div className="aa-pro-metric" style={{ minWidth: 90, textAlign: "center" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "rgba(255,255,255,0.72)" }}>{label}</div>
      <div style={{ fontWeight: 900, color: "#fff", fontSize: 22 }}>{value}</div>
    </div>
  );
}

/* ------------------- Styles ------------------- */
const hero = {
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  padding: "16px 18px",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  marginBottom: 16,
};
const heroLeft = { display: "flex", alignItems: "center", gap: 12 };
const heroIcon = {
  width: 44,
  height: 44,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.35)",
};
const heroTitle = { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 };
const heroSub = { margin: "4px 0 0", opacity: 0.9, fontSize: 13 };
const heroStats = { display: "flex", alignItems: "center", gap: 10 };

const toastBox = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderRadius: 10,
  margin: "12px 0",
  boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
};

const card = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 16,
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  marginBottom: 18,
};

const row = {
  display: "flex",
  gap: 14,
  flexWrap: "wrap",
};

const label = {
  display: "block",
  fontWeight: 800,
  color: COLORS.text,
  marginBottom: 6,
};

const inputWrap = { display: "flex" };

const select = {
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  outline: "none",
  minWidth: 260,
  background: "#fff",
};

const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 260,
};
const searchInput = {
  border: "none",
  outline: "none",
  flex: 1,
  fontSize: 14,
};

const summaryPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  fontWeight: 800,
};

const chipBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "10px 12px",
  borderRadius: 999,
  border: "none",
  cursor: "pointer",
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow: "0 10px 18px rgba(242,12,83,.15)",
  fontWeight: 800,
};
const chipBtnGhost = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "10px 12px",
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  cursor: "pointer",
  color: COLORS.text,
  background: "#fff",
  fontWeight: 700,
};

const chipGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
  gap: 10,
  marginTop: 12,
};

const permChip = {
  width: "100%",
  textAlign: "left",
  padding: "10px 12px",
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};

const noneNote = {
  color: COLORS.subtext,
  background: "#f8fafc",
  border: `1px solid ${COLORS.border}`,
  padding: 10,
  borderRadius: 10,
};

const primaryBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  height: 42,
  padding: "0 16px",
  borderRadius: 12,
  border: "none",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  boxShadow: "0 14px 28px rgba(242,12,83,.18)",
};

const sectionHeader = {
  marginTop: 8,
  marginBottom: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};
const sectionHeaderLeft = { display: "flex", alignItems: "center", gap: 10 };
const sectionIcon = {
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  color: COLORS.primary,
};
const sectionTitle = { fontWeight: 900, color: COLORS.text, fontSize: 18 };
const sectionSub = { color: COLORS.subtext, fontSize: 13, marginTop: 2 };

const cardsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 12,
};

const roleCard = {
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  overflow: "hidden",
  boxShadow: "0 1px 2px rgba(0,0,0,.04)",
};
const roleCardHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 14px",
  background: "#f8fafc",
  borderBottom: `1px solid ${COLORS.border}`,
};
const roleCardBody = { padding: 12 };
const roleChipWrap = { display: "flex", flexWrap: "wrap", gap: 8 };
const roleChip = {
  display: "inline-flex",
  alignItems: "center",
  padding: "6px 10px",
  borderRadius: 999,
  fontSize: 12,
  border: "none",
};

const smallBtn = {
  display: "grid",
  placeItems: "center",
  width: 34,
  height: 34,
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  cursor: "pointer",
};
const smallBtnDanger = {
  ...smallBtn,
  color: "#d32f2f",
  borderColor: "#ffcdd2",
  background: "#ffebee",
};

const deniedWrap = {
  position: "relative",
  minHeight: "70vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: COLORS.page,
  padding: "2rem",
};
const deniedCard = {
  backgroundColor: "white",
  padding: "2rem",
  borderRadius: "12px",
  boxShadow: "0 0 20px rgba(0,0,0,0.15)",
  textAlign: "center",
  maxWidth: "420px",
};
